<template>
    <vx-expandable-card class="mb-4">
        <template v-slot:header>Pricing & Commitments</template>
        <template v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <div v-if="!billingItemsLoading && billingItems.length > 0" class="flex flex-col gap-4">
                    <fieldset
                        class="inline-flex items-center justify-center gap-0.5 bg-gray-100 rounded-lg p-0.5"
                        data-test="month-tabs"
                    >
                        <legend class="sr-only">Billing month</legend>
                        <label
                            v-for="(item, index) in billingItems"
                            :key="index"
                            class="relative flex-1"
                            :data-test="`month-tab-${index === 0 ? 'previous' : index === 1 ? 'current' : 'next'}`"
                        >
                            <input
                                type="radio"
                                :value="index"
                                v-model="selectedMonth"
                                :name="billingMonthName"
                                :disabled="isEditingBillingItems"
                                class="peer sr-only"
                            />
                            <div
                                class="rounded-md px-4 py-2.5 text-center transition-all duration-200 text-gray-600 hover:text-gray-900 peer-checked:bg-white peer-checked:text-gray-900 peer-checked:shadow-sm peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-disabled:opacity-50 peer-disabled:hover:text-gray-600"
                                :class="isEditingBillingItems ? 'cursor-not-allowed' : 'cursor-pointer'"
                            >
                                <div class="flex flex-col items-center gap-0.5">
                                    <span
                                        class="text-xs uppercase tracking-wider font-medium transition-colors"
                                        :class="index === 1 ? 'text-blue-600' : 'text-gray-600'"
                                    >
                                        {{ index === 0 ? 'Previous' : index === 1 ? 'Current' : 'Next' }}
                                    </span>
                                    <span class="text-sm font-semibold">
                                        {{ formatMonthYear(item.period) }}
                                    </span>
                                </div>
                            </div>
                        </label>
                    </fieldset>

                    <div v-if="team.status !== 'closed'" class="h-10 flex justify-between items-center">
                        <div
                            v-if="selectedMonth === 0 && !canEditPreviousPeriod() && currentBillingItem"
                            class="flex items-center gap-2 text-sm text-gray-500"
                        >
                            <svg
                                class="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-label="Lock icon"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                ></path>
                            </svg>
                            <span>Historical rates cannot be modified</span>
                        </div>
                        <div v-else class="flex-1"></div>

                        <div v-if="(selectedMonth !== 0 || canEditPreviousPeriod()) && currentBillingItem">
                            <div v-if="!isEditingBillingItems">
                                <vx-button
                                    size="sm"
                                    color="primary"
                                    @click="startEditingBillingItems"
                                    data-test="edit-button"
                                >
                                    Adjust
                                </vx-button>
                            </div>
                            <div v-else class="flex gap-2">
                                <vx-button
                                    size="sm"
                                    color="primary"
                                    @click="saveBillingItems"
                                    :loading="savingBillingItems"
                                    data-test="save-button"
                                >
                                    Save Changes
                                </vx-button>
                                <vx-button
                                    size="sm"
                                    color="muted"
                                    @click="cancelEditingBillingItems"
                                    :disabled="savingBillingItems"
                                    data-test="cancel-button"
                                >
                                    Cancel
                                </vx-button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Explanatory text for editing effects -->
                <div v-if="editingEffectDescription && team.status !== 'closed'" class="mt-3 mx-4">
                    <vx-alert color="warning" :closeable="false">
                        <template v-slot:icon>
                            <font-awesome-icon widthAuto :icon="faCircleInfo" />
                        </template>
                        {{ editingEffectDescription }}
                    </vx-alert>
                </div>

                <div v-if="billingItemsLoading" class="py-3 text-center">
                    <vx-icon-loading class="w-9 h-9" spin></vx-icon-loading>
                </div>
                <div v-else-if="billingItemsError">
                    <vx-alert color="danger" :closeable="false">
                        {{ billingItemsError }}
                    </vx-alert>
                </div>
                <div v-else-if="currentBillingItem" class="space-y-6">
                    <kiosk-base-fees
                        v-model:values="editingValues"
                        v-model:listed="editingBaseFeeKeys"
                        :fees="ROUTED_BASE_FEES"
                        :routingOptions="GROUP_ROUTING_OPTIONS"
                        :item="currentBillingItem"
                        :editing="isEditingBillingItems"
                        :errors="billingItemsValidationErrors"
                    />

                    <!-- Messaging Section -->
                    <div>
                        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                            Messaging
                        </div>
                        <div class="rounded-lg border border-gray-200 bg-white transition-all duration-200">
                            <!-- Side-by-side layout -->
                            <div class="flex" :class="isEditingBillingItems ? '' : 'divide-x divide-gray-200'">
                                <!-- Group-sent (Left) -->
                                <div class="flex flex-1">
                                    <div
                                        class="w-12 flex items-center justify-center rounded-tl-lg"
                                        :class="[
                                            groupUsageStyle.background,
                                            isEditingBillingItems ? '' : 'rounded-bl-lg',
                                        ]"
                                    >
                                        <font-awesome-icon
                                            :icon="groupUsageStyle.icon"
                                            :class="groupUsageStyle.color"
                                        />
                                    </div>
                                    <div
                                        class="flex-1 flex items-center px-6"
                                        :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                    >
                                        <span :id="groupUsageLabelId" class="text-gray-700">Group-sent</span>
                                    </div>
                                </div>

                                <!-- Non-Group-sent (Right) -->
                                <div class="flex flex-1">
                                    <div
                                        class="w-12 flex items-center justify-center"
                                        :class="nonGroupUsageStyle.background"
                                    >
                                        <font-awesome-icon
                                            :icon="nonGroupUsageStyle.icon"
                                            :class="nonGroupUsageStyle.color"
                                        />
                                    </div>
                                    <div
                                        class="flex-1 flex items-center px-6"
                                        :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                    >
                                        <span :id="nonGroupUsageLabelId" class="text-gray-700">Non-Group-sent</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Routing configuration when editing -->
                            <div
                                v-if="isEditingBillingItems"
                                class="grid grid-cols-2 gap-6 px-6 py-3 bg-gray-50 rounded-b-lg border-t border-gray-200"
                            >
                                <!-- Group-sent routing -->
                                <div class="flex flex-col gap-2">
                                    <div
                                        role="radiogroup"
                                        :aria-labelledby="groupUsageLabelId"
                                        :aria-describedby="groupUsageRoutingDescriptionId"
                                        class="flex gap-4"
                                        data-test="group-usage-routing"
                                    >
                                        <label class="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                v-model="editingValues.group_usage_routing"
                                                :name="groupUsageRoutingName"
                                                value="group"
                                                class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                data-test="group-usage-routing-group"
                                            />
                                            <span class="text-sm text-gray-700">Group Pays</span>
                                        </label>
                                        <label class="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                v-model="editingValues.group_usage_routing"
                                                :name="groupUsageRoutingName"
                                                value="rollup"
                                                class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                data-test="group-usage-routing-rollup"
                                            />
                                            <span class="text-sm text-gray-700"
                                                >Roll up to the next billable level</span
                                            >
                                        </label>
                                    </div>
                                    <div :id="groupUsageRoutingDescriptionId" class="text-xs text-gray-500">
                                        Applies to messages sent by this group (group sender type).
                                    </div>
                                </div>

                                <!-- Non-Group-sent routing -->
                                <div class="flex flex-col gap-2">
                                    <div
                                        role="radiogroup"
                                        :aria-labelledby="nonGroupUsageLabelId"
                                        :aria-describedby="nonGroupUsageRoutingDescriptionId"
                                        class="flex gap-4"
                                        data-test="non-group-usage-routing"
                                    >
                                        <label class="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                v-model="editingValues.non_group_usage_routing"
                                                :name="nonGroupUsageRoutingName"
                                                value="group"
                                                class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                data-test="non-group-usage-routing-group"
                                            />
                                            <span class="text-sm text-gray-700">Group Pays</span>
                                        </label>
                                        <label class="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                v-model="editingValues.non_group_usage_routing"
                                                :name="nonGroupUsageRoutingName"
                                                value="rollup"
                                                class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                data-test="non-group-usage-routing-rollup"
                                            />
                                            <span class="text-sm text-gray-700"
                                                >Roll up to the next billable level</span
                                            >
                                        </label>
                                    </div>
                                    <div :id="nonGroupUsageRoutingDescriptionId" class="text-xs text-gray-500">
                                        Applies to usage attributed to this group that the group did not send (corporate
                                        or OU sender type).
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Minimum Spend Commitment - Separate section -->
                    <div>
                        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                            Commitments
                        </div>
                        <div class="rounded-lg border border-gray-200 bg-white transition-all duration-200">
                            <div class="divide-y divide-gray-200">
                                <div class="flex">
                                    <div
                                        class="w-12 flex items-center justify-center"
                                        :class="[
                                            (isEditingBillingItems
                                                ? parseFloat(editingValues.minimum_spend_commit) || 0
                                                : currentBillingItem.minimum_spend_commit) === 0
                                                ? 'bg-gray-200'
                                                : 'bg-sky-100',
                                            isEditingBillingItems ? 'rounded-tl-lg' : 'rounded-l-lg',
                                        ]"
                                    >
                                        <font-awesome-icon
                                            :icon="
                                                (isEditingBillingItems
                                                    ? parseFloat(editingValues.minimum_spend_commit) || 0
                                                    : currentBillingItem.minimum_spend_commit) === 0
                                                    ? faBan
                                                    : faStore
                                            "
                                            :class="
                                                (isEditingBillingItems
                                                    ? parseFloat(editingValues.minimum_spend_commit) || 0
                                                    : currentBillingItem.minimum_spend_commit) === 0
                                                    ? 'text-gray-500'
                                                    : 'text-sky-600'
                                            "
                                        />
                                    </div>
                                    <div
                                        class="flex-1 flex justify-between items-center px-6"
                                        :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                    >
                                        <div class="flex items-center gap-2">
                                            <span class="text-gray-700">Minimum Spend Commitment</span>
                                            <vx-badge
                                                v-if="
                                                    !isEditingBillingItems &&
                                                    currentBillingItem.minimum_spend_commit > 0
                                                "
                                                size="xs"
                                                :color="
                                                    currentBillingItem.minimum_spend_includes_base_fees
                                                        ? 'primary'
                                                        : 'amber'
                                                "
                                            >
                                                {{
                                                    currentBillingItem.minimum_spend_includes_base_fees
                                                        ? 'Includes base'
                                                        : 'Usage only'
                                                }}
                                            </vx-badge>
                                        </div>
                                        <div class="flex flex-col items-end">
                                            <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                                {{ formatMoney(currentBillingItem.minimum_spend_commit) }}
                                            </span>
                                            <vx-input
                                                v-else
                                                v-model="editingValues.minimum_spend_commit"
                                                type="number"
                                                :step="0.00001"
                                                :min="0"
                                                :max="1000000"
                                                size="sm"
                                                class="w-40! text-right font-mono"
                                                placeholder="0.00000"
                                                @blur="formatEditingValue('minimum_spend_commit')"
                                                :error="billingItemsValidationErrors.minimum_spend_commit"
                                                data-test="billing-item-input-minimum-spend-commit"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <!-- Checkbox for base fees inclusion - show when editing -->
                                <div
                                    v-if="isEditingBillingItems"
                                    class="flex items-start gap-3 px-6 py-3 bg-gray-50 rounded-b-lg"
                                >
                                    <input
                                        v-model="editingValues.minimum_spend_includes_base_fees"
                                        type="checkbox"
                                        id="minimum-spend-includes-base-group"
                                        class="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        data-test="minimum-spend-includes-base-fees-checkbox"
                                    />
                                    <label for="minimum-spend-includes-base-group" class="flex-1">
                                        <div class="text-sm text-gray-700">
                                            Include base fees in minimum spend calculation
                                        </div>
                                        <div class="text-xs text-gray-500 mt-1">
                                            When checked, base fees allocated to the group count toward the minimum
                                            spend. Usage that rolls up does not count.
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div class="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                            Corporate Credit
                        </div>
                        <div class="rounded-lg border border-gray-200 bg-white transition-all duration-200">
                            <div class="flex">
                                <div
                                    class="w-12 flex items-center justify-center rounded-l-lg"
                                    :class="
                                        (isEditingBillingItems
                                            ? parseFloat(editingValues.corporate_credit) || 0
                                            : currentBillingItem.corporate_credit) === 0
                                            ? 'bg-gray-200'
                                            : 'bg-amber-100'
                                    "
                                >
                                    <font-awesome-icon
                                        :icon="
                                            (isEditingBillingItems
                                                ? parseFloat(editingValues.corporate_credit) || 0
                                                : currentBillingItem.corporate_credit) === 0
                                                ? faBan
                                                : faCrown
                                        "
                                        :class="
                                            (isEditingBillingItems
                                                ? parseFloat(editingValues.corporate_credit) || 0
                                                : currentBillingItem.corporate_credit) === 0
                                                ? 'text-gray-500'
                                                : 'text-amber-600'
                                        "
                                    />
                                </div>
                                <div
                                    class="flex-1 flex justify-between items-center px-6"
                                    :class="isEditingBillingItems ? 'py-3' : 'py-4'"
                                >
                                    <div class="flex items-center gap-2">
                                        <span class="text-gray-700">Corporate Credit</span>
                                        <vx-badge
                                            v-if="!isEditingBillingItems && currentBillingItem.corporate_credit > 0"
                                            size="xs"
                                            color="amber"
                                        >
                                            Corporate pays
                                        </vx-badge>
                                    </div>
                                    <div class="flex flex-col items-end">
                                        <span v-if="!isEditingBillingItems" class="font-mono text-gray-900">
                                            {{ formatMoney(currentBillingItem.corporate_credit) }}
                                        </span>
                                        <vx-input
                                            v-else
                                            v-model="editingValues.corporate_credit"
                                            type="number"
                                            :step="0.00001"
                                            :min="0"
                                            :max="1000000"
                                            size="sm"
                                            class="w-40! text-right font-mono"
                                            placeholder="0.00000"
                                            @blur="formatEditingValue('corporate_credit')"
                                            :error="billingItemsValidationErrors.corporate_credit"
                                            data-test="billing-item-input-corporate-credit"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="isEditingBillingItems"
                                class="px-6 py-3 bg-gray-50 rounded-b-lg border-t border-gray-200 text-xs text-gray-500"
                            >
                                Always billed to corporate and applied as a credit against this group's SMS &amp; MMS
                                usage; any remaining credit then offsets the group's SMS &amp; MMS carrier fees
                                (messaging usage first, then carrier fees).
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { ref, computed, onMounted, useId } from 'vue';
import { VxButton, VxExpandableCard, VxInput, VxIconLoading, VxAlert, VxBadge } from '@voxie/frontend-components';
import adminGroupsClient from '~/services/adminGroupsClient';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { useBillingDates } from '~/composables/useBillingDates';
import { faCircleInfo, faCrown, faStore, faBan } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatNumber } from '~/utils/helpers';
import KioskBaseFees from '~/components/kiosk/kiosk-billing/KioskBaseFees.vue';
import {
    GROUP_ROUTING_OPTIONS,
    ROUTED_BASE_FEES,
    formatMoney,
    isBaseFeeInUse,
    payerStyle,
    validateListedBaseFees,
} from '~/components/kiosk/kiosk-billing/baseFees';

const props = defineProps({
    team: {
        type: Object,
        required: true,
    },
    teamId: {
        type: Number,
        required: true,
    },
    group: {
        type: Object,
        required: true,
    },
    groupId: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(['updated']);

const toasted = useToasted();
const { formatMonthYear, canEditPreviousPeriod } = useBillingDates();

const billingItems = ref([]);
const billingItemsLoading = ref(false);
const billingItemsError = ref(null);
const selectedMonth = ref(1);
const isEditingBillingItems = ref(false);
const editingValues = ref({});
const editingBaseFeeKeys = ref([]);
const savingBillingItems = ref(false);
const billingItemsValidationErrors = ref({});

const billingMonthName = useId();
const groupUsageLabelId = useId();
const groupUsageRoutingName = useId();
const groupUsageRoutingDescriptionId = useId();
const nonGroupUsageLabelId = useId();
const nonGroupUsageRoutingName = useId();
const nonGroupUsageRoutingDescriptionId = useId();

const currentBillingItem = computed(() => {
    return billingItems.value[selectedMonth.value] || null;
});

const groupUsageStyle = computed(() =>
    payerStyle(
        isEditingBillingItems.value
            ? editingValues.value.group_usage_routing
            : currentBillingItem.value?.group_usage_routing
    )
);

const nonGroupUsageStyle = computed(() =>
    payerStyle(
        isEditingBillingItems.value
            ? editingValues.value.non_group_usage_routing
            : currentBillingItem.value?.non_group_usage_routing
    )
);

const editingEffectDescription = computed(() => {
    if (!isEditingBillingItems.value) {
        return '';
    }

    if (selectedMonth.value === 2) {
        return 'Changes will apply to this and all future months.';
    }

    return 'Changes will apply to this and all future months, unless they have already been customized.';
});

const startEditingBillingItems = () => {
    if (!currentBillingItem.value) {
        return;
    }

    editingValues.value = {
        license_fee: currentBillingItem.value.license_fee.toFixed(5),
        license_fee_routing: currentBillingItem.value.license_fee_routing,
        professional_services_fee: currentBillingItem.value.professional_services_fee.toFixed(5),
        professional_services_fee_routing: currentBillingItem.value.professional_services_fee_routing,
        location_fee_quantity: String(currentBillingItem.value.location_fee_quantity || 0),
        location_fee_rate: (currentBillingItem.value.location_fee_rate || 0).toFixed(5),
        location_fee_routing: currentBillingItem.value.location_fee_routing,
        minimum_spend_commit: currentBillingItem.value.minimum_spend_commit.toFixed(5),
        minimum_spend_includes_base_fees: currentBillingItem.value.minimum_spend_includes_base_fees ?? true,
        corporate_credit: (currentBillingItem.value.corporate_credit || 0).toFixed(5),
        group_usage_routing: currentBillingItem.value.group_usage_routing || 'rollup',
        non_group_usage_routing: currentBillingItem.value.non_group_usage_routing || 'rollup',
    };
    editingBaseFeeKeys.value = ROUTED_BASE_FEES.filter((fee) => isBaseFeeInUse(fee, currentBillingItem.value)).map(
        (fee) => fee.key
    );

    billingItemsValidationErrors.value = {};
    isEditingBillingItems.value = true;
};

const cancelEditingBillingItems = () => {
    isEditingBillingItems.value = false;
    editingValues.value = {};
    editingBaseFeeKeys.value = [];
    billingItemsValidationErrors.value = {};
};

const formatEditingValue = (field) => {
    const value = parseFloat(editingValues.value[field]);
    if (!isNaN(value)) {
        editingValues.value[field] = value.toFixed(5);
    }
};

const validateQuantityField = (rawValue) => {
    const intValue = parseInt(rawValue);

    if (String(intValue) !== String(rawValue)) {
        return 'Quantity must be a whole number';
    } else if (isNaN(intValue)) {
        return 'Invalid number';
    } else if (intValue < 0) {
        return 'Cannot be negative';
    } else if (intValue > 10000) {
        return 'Cannot exceed 10,000';
    }
    return null;
};

const validateAmountField = (value) => {
    if (isNaN(value)) {
        return 'Invalid number';
    }

    if (value < 0) {
        return 'Cannot be negative';
    }

    if (value > 1000000) {
        return `Cannot exceed $${formatNumber(1000000)}`;
    }

    return null;
};

const validateBillingItems = () => {
    const errors = {};

    Object.keys(editingValues.value).forEach((field) => {
        if (
            field === 'minimum_spend_includes_base_fees' ||
            field === 'license_fee_routing' ||
            field === 'professional_services_fee_routing' ||
            field === 'location_fee_routing' ||
            field === 'group_usage_routing' ||
            field === 'non_group_usage_routing'
        ) {
            return;
        } else if (field === 'location_fee_quantity') {
            const error = validateQuantityField(editingValues.value[field]);
            if (error) {
                errors[field] = error;
            }
        } else {
            const value = parseFloat(editingValues.value[field]);
            const error = validateAmountField(value);
            if (error) {
                errors[field] = error;
            }
        }
    });

    billingItemsValidationErrors.value = {
        ...validateListedBaseFees(ROUTED_BASE_FEES, editingBaseFeeKeys.value, editingValues.value),
        ...errors,
    };
    return Object.keys(billingItemsValidationErrors.value).length === 0;
};

const saveBillingItems = async () => {
    if (savingBillingItems.value || !currentBillingItem.value) {
        return;
    }

    if (!validateBillingItems()) {
        return;
    }

    savingBillingItems.value = true;

    try {
        // Normalize location fees
        const locationFeeQuantity = parseInt(editingValues.value.location_fee_quantity) || 0;
        const locationFeeRate = parseFloat(editingValues.value.location_fee_rate);

        const data = {
            effective_date: currentBillingItem.value.period,
            license_fee: parseFloat(editingValues.value.license_fee),
            license_fee_routing: editingValues.value.license_fee_routing,
            professional_services_fee: parseFloat(editingValues.value.professional_services_fee),
            professional_services_fee_routing: editingValues.value.professional_services_fee_routing,
            location_fee_quantity: locationFeeQuantity === 0 || locationFeeRate === 0 ? 0 : locationFeeQuantity,
            location_fee_rate: locationFeeQuantity === 0 || locationFeeRate === 0 ? 0 : locationFeeRate,
            location_fee_routing: editingValues.value.location_fee_routing,
            minimum_spend_commit: parseFloat(editingValues.value.minimum_spend_commit),
            minimum_spend_includes_base_fees: editingValues.value.minimum_spend_includes_base_fees ?? true,
            corporate_credit: parseFloat(editingValues.value.corporate_credit),
            group_usage_routing: editingValues.value.group_usage_routing,
            non_group_usage_routing: editingValues.value.non_group_usage_routing,
        };

        await adminGroupsClient.setPricing(props.teamId, props.groupId, data);

        await fetchPricing(false);

        emit('updated');

        toasted.success('Pricing & commitments have been successfully updated');

        isEditingBillingItems.value = false;
        editingValues.value = {};
        editingBaseFeeKeys.value = [];
        billingItemsValidationErrors.value = {};
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        savingBillingItems.value = false;
    }
};

const fetchPricing = async (showLoading = true) => {
    if (showLoading) {
        billingItemsLoading.value = true;
    }
    billingItemsError.value = null;

    try {
        const response = await adminGroupsClient.getPricing(props.teamId, props.groupId);
        billingItems.value = response?.data?.items || response?.items || [];
    } catch (e) {
        billingItemsError.value = 'Failed to load pricing';
        toasted.error(catchRequestError(e));
        console.error(e);
    } finally {
        if (showLoading) {
            billingItemsLoading.value = false;
        }
    }
};

onMounted(() => {
    fetchPricing();
});
</script>
